// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { RemoveLineItemDialog } from '@/billing/components/remove-line-item-dialog';
import {
  LineItemsFormState,
  compactInputSx,
  computeLineTotal,
  makeLineItemDragData,
  parseFormAmount,
} from '@/billing/utilities/line-item-helpers';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDateRangePicker } from '@/components/form-date-range-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import {
  DeleteRounded,
  DragIndicatorRounded,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { fromMinorUnits, toMinorUnits } from '@/currency/utilities/math';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { Decimal } from 'decimal.js';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PerUnitAdjustment {
  previousTotal: Decimal;
  // The unit price and quantity the adjustment was made for.
  unitPriceAmount: string;
  quantity: string;
}

interface EditableLineItemRowProps {
  index: number;
  control: Control<LineItemsFormState>;
  setValue: UseFormSetValue<LineItemsFormState>;
  onSwap: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
  dragSourceIndexRef: React.MutableRefObject<number | null>;
  currencyCode: IsoCurrencyCode;
}

// ---------------------------------------------------------------------------
// Editable row — uses react-hook-form controlled components:
// FormTextField, FormNumberTextField, FormDatePicker
// ---------------------------------------------------------------------------

// Memoized so parent-level re-renders (e.g., a totals watcher up the tree)
// don't cascade into every row on every keystroke. All props are stable
// references — control/setValue/dragSourceIndexRef are refs, onSwap/onRemove
// come from useFieldArray, and index/key only change on structural edits.
export const EditableLineItemRow = memo(EditableLineItemRowImpl);

function EditableLineItemRowImpl(props: EditableLineItemRowProps) {
  const {
    index,
    control,
    setValue,
    onSwap,
    onRemove,
    dragSourceIndexRef,
    currencyCode,
  } = props;

  const rowRef = useRef<HTMLTableRowElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const key = useWatch({ control, name: `lineItems.${index}.key` });
  const description = useWatch({
    control,
    name: `lineItems.${index}.description`,
  });
  const pricingMode = useWatch({
    control,
    name: `lineItems.${index}.pricingMode`,
  });
  const quantity = useWatch({
    control,
    name: `lineItems.${index}.quantity`,
  });
  const unitPrice = useWatch({
    control,
    name: `lineItems.${index}.unitPrice`,
  });
  const totalPrice = useWatch({
    control,
    name: `lineItems.${index}.totalPrice`,
  });
  const taxAmount = useWatch({
    control,
    name: `lineItems.${index}.taxAmount`,
  });

  const removeDialog = useDisclosure();

  const handleConfirmRemove = useCallback(() => {
    removeDialog.close();
    onRemove(index);
  }, [removeDialog, onRemove, index]);

  const isPerUnit = pricingMode === 'PER_UNIT';

  // The FIXED total a switch to per-unit pricing could not keep, shown until
  // the user edits the derived unit price or quantity.
  const [perUnitAdjustment, setPerUnitAdjustment] =
    useState<PerUnitAdjustment | null>(null);
  const isPerUnitAdjustmentShown =
    perUnitAdjustment !== null &&
    perUnitAdjustment.unitPriceAmount === unitPrice.amount &&
    perUnitAdjustment.quantity === quantity;

  // Auto-compute totalPrice for PER_UNIT: qty × unitPrice. An empty unitPrice
  // propagates to an empty totalPrice (so qty × nothing = nothing, not $0.00).
  // An unparseable operand leaves the total for validation to flag.
  useEffect(() => {
    if (!isPerUnit) return;
    if (unitPrice.amount === '') {
      if (totalPrice.amount !== '') {
        setValue(`lineItems.${index}.totalPrice.amount`, '');
      }

      return;
    }

    const qty = parseCurrencyAmount(quantity === '' ? '0' : quantity);
    const price = parseCurrencyAmount(unitPrice.amount);
    if (qty === null || price === null) return;

    const computed = padAmountToMinorUnit(price.times(qty), currencyCode);
    if (computed !== totalPrice.amount) {
      setValue(`lineItems.${index}.totalPrice.amount`, computed);
    }
  }, [
    isPerUnit,
    quantity,
    unitPrice,
    totalPrice,
    index,
    setValue,
    currencyCode,
  ]);

  // We derive a unit price from the FIXED total; when the total does not
  // divide evenly, the unit price rounds half-up and the new total (unit
  // price × quantity) is flagged to the user as changed.
  const handleSwitchToPerUnit = useCallback(() => {
    const parsedQuantity = parseCurrencyAmount(quantity);
    const qty =
      parsedQuantity === null || parsedQuantity.isZero()
        ? new CurrencyDecimal(1)
        : parsedQuantity;
    const amount =
      parseCurrencyAmount(totalPrice.amount) ?? new CurrencyDecimal(0);
    const derivedUnitPrice = fromMinorUnits(
      toMinorUnits(amount, currencyCode).dividedToIntegerBy(qty),
      currencyCode
    );
    const unitPriceAmount = padAmountToMinorUnit(
      derivedUnitPrice,
      currencyCode
    );
    setValue(`lineItems.${index}.pricingMode`, 'PER_UNIT');
    setValue(`lineItems.${index}.unitPrice.amount`, unitPriceAmount);
    setPerUnitAdjustment(
      derivedUnitPrice.times(qty).equals(amount)
        ? null
        : { previousTotal: amount, unitPriceAmount, quantity }
    );
  }, [index, quantity, totalPrice, setValue, currencyCode]);

  const handleSwitchToFixed = useCallback(() => {
    setPerUnitAdjustment(null);
    setValue(`lineItems.${index}.pricingMode`, 'FIXED');
    setValue(`lineItems.${index}.unitPrice.amount`, '');
  }, [index, setValue]);

  useEffect(() => {
    const row = rowRef.current;
    const handle = handleRef.current;
    if (!row || !handle) {
      return;
    }

    const dragData = makeLineItemDragData(index, key);

    // The drag source is the HANDLE only, not the row. Using `element: row`
    // plus `dragHandle: handle` would set draggable=true on the entire row,
    // which puts every child input into the HTML5 drag-initiation context —
    // that breaks text selection inside the inputs and adds per-keystroke
    // lag as the browser evaluates every mousemove as a potential drag.
    const cleanupDraggable = draggable({
      element: handle,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
      getInitialData: () => dragData,
    });

    // The row remains the drop target so swaps trigger as the user drags
    // the handle over any part of another row.
    const cleanupDropTarget = dropTargetForElements({
      element: row,
      getData: () => dragData,
      canDrop: () => {
        const fromIndex = dragSourceIndexRef.current;
        return fromIndex !== null && fromIndex !== index;
      },
      onDragEnter: () => {
        const fromIndex = dragSourceIndexRef.current;
        if (fromIndex === null || fromIndex === index) return;
        onSwap(fromIndex, index);
        dragSourceIndexRef.current = index;
      },
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [index, key, onSwap, dragSourceIndexRef]);

  const lineTotal = computeLineTotal(totalPrice.amount, taxAmount.amount);

  return (
    <TableRow
      ref={rowRef}
      sx={{
        opacity: isDragging ? 0.65 : 1,
      }}
    >
      {/* Drag handle */}
      <TableCell>
        <IconButton ref={handleRef} size="small" sx={{ cursor: 'grab' }}>
          <DragIndicatorRounded fontSize="small" />
        </IconButton>
      </TableCell>

      {/* Description */}
      <TableCell>
        <FormTextField
          control={control}
          name={`lineItems.${index}.description`}
          fullWidth
        />
      </TableCell>

      {/* Service period */}
      <TableCell sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
        <FormDateRangePicker
          control={control}
          name={`lineItems.${index}.servicePeriod`}
          label=""
        />
      </TableCell>

      {/* Qty */}
      <TableCell align="right">
        <FormNumberTextField
          control={control}
          name={`lineItems.${index}.quantity`}
          fullWidth
          InputProps={{ sx: compactInputSx }}
        />
      </TableCell>

      {/* Unit price */}
      <TableCell align="right">
        {isPerUnit ? (
          <FormCurrencyTextField
            control={control}
            name={`lineItems.${index}.unitPrice`}
            lockTo={currencyCode}
            compact
            hideRightAdornment
            fullWidth
          />
        ) : (
          <Typography
            variant="caption"
            color="primary"
            sx={{
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={handleSwitchToPerUnit}
          >
            Set unit price
          </Typography>
        )}
      </TableCell>

      {/* Amount */}
      <TableCell align="right">
        {isPerUnit ? (
          <>
            <Typography variant="body2">
              {formatAmountWithCurrency(
                parseFormAmount(totalPrice.amount),
                currencyCode,
                { sparse: true }
              )}
            </Typography>
            {isPerUnitAdjustmentShown && (
              <Typography
                variant="caption"
                color="warning.main"
                display="block"
              >
                {`Adjusted from ${formatCurrencyValue({
                  amount: perUnitAdjustment.previousTotal,
                  isoCode: currencyCode,
                })} to fit the unit price`}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={handleSwitchToFixed}
            >
              Override
            </Typography>
          </>
        ) : (
          <FormCurrencyTextField
            control={control}
            name={`lineItems.${index}.totalPrice`}
            lockTo={currencyCode}
            compact
            hideRightAdornment
            fullWidth
          />
        )}
      </TableCell>

      {/* Tax */}
      <TableCell align="right">
        <FormCurrencyTextField
          control={control}
          name={`lineItems.${index}.taxAmount`}
          lockTo={currencyCode}
          compact
          hideRightAdornment
          fullWidth
        />
      </TableCell>

      {/* Line total (computed, read-only) */}
      <TableCell align="right">
        <Typography variant="body2" fontWeight="bold">
          {formatAmountWithCurrency(lineTotal, currencyCode)}
        </Typography>
      </TableCell>

      {/* Delete */}
      <TableCell sx={{ width: 40 }}>
        <IconButton
          size="small"
          onClick={removeDialog.open}
          aria-label="Delete"
        >
          <DeleteRounded fontSize="small" />
        </IconButton>
        <RemoveLineItemDialog
          isOpen={removeDialog.isOpen}
          preview={{
            description,
            pricingMode,
            quantity,
            unitPrice: unitPrice.amount === '' ? null : unitPrice.amount,
            totalPrice: totalPrice.amount === '' ? null : totalPrice.amount,
            taxAmount: taxAmount.amount === '' ? null : taxAmount.amount,
          }}
          currencyCode={currencyCode}
          onClose={removeDialog.close}
          onConfirm={handleConfirmRemove}
        />
      </TableCell>
    </TableRow>
  );
}
